import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {showLoadingBar, showMessages} from 'app/store/sweet-api/utils';
import {
  ACTIVITY_API_URL,
  SIGNOFF_API_URL,
  SIGNOFF_DOCUMENT_API_URL,
  SIGNOFFTEMPLATE_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {SIGNOFF_TAGS, TEAMTAG} from 'app/utils/rtk-tags';

import type {DocumentVersionContentType} from 'app/acceptance/types';
import type {
  ActivitySignoffRequestedType,
  CreateSignOffParams,
  SignoffFetchParamsType,
  SignoffTemplateFetchParamsType,
  UpdateSignOffParams,
} from 'app/signoffs/types';
import type {
  CompletedUserJobSerializedType,
  ListResponseType,
  SignoffDocumentSerializedType,
  SignoffSerializedType,
  SignoffTemplateSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [SIGNOFF_TAGS.TAGS],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      signoffs: build.query<
        ListResponseType<SignoffSerializedType>,
        SignoffFetchParamsType
      >({
        query: ({page_size = DEFAULT_PAGE_SIZE, ...others}) => ({
          url: SIGNOFF_API_URL,
          params: {page_size, ...others},
        }),
        providesTags: () => [{type: SIGNOFF_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not fetch signoffs.'),
          })(args, api);
        },
      }),
      signoffDocument: build.mutation<
        unknown,
        {
          url: string;
          version_id: number;
          signed_text: string;
          version_content_type: DocumentVersionContentType;
        }
      >({
        query: ({url, ...body}) => ({
          method: 'PATCH',
          url,
          body,
        }),
        invalidatesTags: () => [{type: SIGNOFF_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Unable to sign off the document.'),
              byStatus: {
                400: (response) =>
                  response && Object.values(response).flat().join(', '),
              },
            },
            onSuccess: () =>
              gt('Thanks, your Sign Off was successfully registered.'),
          })(args, api);
        },
      }),
      documentSignoffs: build.query<
        ListResponseType<SignoffDocumentSerializedType>,
        SignoffTemplateFetchParamsType
      >({
        query: (params) => ({
          url: SIGNOFF_DOCUMENT_API_URL,
          params,
        }),
      }),
      getSignoffRequestActivities: build.query<
        ListResponseType<ActivitySignoffRequestedType>,
        {signoff_id: number}
      >({
        query: ({signoff_id}) => ({
          url: ACTIVITY_API_URL,
          params: {
            verb: 'requested signoff',
            action_object_object_id: signoff_id,
            action_object_content_type: 'signoff',
          },
        }),
      }),
      createSignoffTemplate: build.mutation<
        CompletedUserJobSerializedType,
        CreateSignOffParams
      >({
        queryFn: (
          {team, rules},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            CompletedUserJobSerializedType,
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: SIGNOFFTEMPLATE_API_URL,
                    method: 'POST',
                    body: {
                      team: team.url,
                      rules,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: TEAMTAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onStart: () => gt('Creating Signoff...'),
            onSuccess: () => [
              gt('Signoff created successfully.'),
              {level: 'success'},
            ],
            onError: () =>
              gt("There was an error and we couldn't create the signoff."),
          })(args, api);
        },
      }),
      updateSignoffTemplate: build.mutation<
        SignoffTemplateSerializedType,
        UpdateSignOffParams
      >({
        query: ({url, ...rules}) => ({
          method: 'PATCH',
          url,
          body: rules,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Sign Off settings failed to update.'),
            onSuccess: () => [
              gt('Sign Off settings updated.'),
              {level: 'success'},
            ],
          })(args, api);
        },
      }),
      getSignoffTemplate: build.query<
        SignoffTemplateSerializedType,
        {url: string}
      >({
        query: ({url}) => ({
          url,
        }),
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useSignoffsQuery: useSignoffs,
  useDocumentSignoffsQuery: useDocumentSignoffs,
  useSignoffDocumentMutation: useSignoffDocument,
  useGetSignoffRequestActivitiesQuery: useSignoffRequestActivities,
  useCreateSignoffTemplateMutation: useCreateSignoffTemplate,
  useUpdateSignoffTemplateMutation: useUpdateSignoffTemplate,
  useGetSignoffTemplateQuery: useGetSignoffTemplate,
} = extendedAPI;
